import { z } from 'zod';

// URL validation helper
const urlSchema = z.string().refine(
  (val) => {
    if (!val) return true; // Optional URL
    try {
      const url = new URL(val.startsWith('http') ? val : `https://${val}`);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  },
  { message: 'Please enter a valid website URL' }
);

export const companyInformationSchema = z.object({
  companyName: z.string()
    .min(2, 'Company name must be at least 2 characters')
    .max(100, 'Company name must be less than 100 characters'),
  
  website: urlSchema.optional(),
  
  industries: z.array(z.string())
    .min(1, 'Please select at least one industry'),
  
  companySize: z.string()
    .min(1, 'Please select company size'),
  
  productDescription: z.string()
    .min(10, 'Please provide at least 10 characters for product description')
    .max(500, 'Product description must be less than 500 characters'),
  
  valueProposition: z.string()
    .min(10, 'Please provide at least 10 characters for value proposition')
    .max(300, 'Value proposition must be less than 300 characters'),
});

export const targetCriteriaSchema = z.object({
  companySize: z.array(z.string())
    .min(1, 'Please select at least one company size'),
  
  location: z.array(z.string())
    .min(1, 'Please select at least one location'),
  
  industries: z.array(z.string())
    .min(1, 'Please select at least one industry'),
  
  jobTitles: z.array(z.string())
    .min(1, 'Please select at least one job title'),
  
  departments: z.array(z.string())
    .min(1, 'Please select at least one department'),
  
  painPoints: z.array(z.string())
    .min(1, 'Please identify at least one pain point'),
  
  buyingSignals: z.array(z.string())
    .min(1, 'Please select at least one buying signal'),
  
  targetDescription: z.string()
    .min(50, 'Please provide a more detailed description (at least 50 characters)')
    .max(1000, 'Description must be less than 1000 characters'),
});

export const campaignGoalsSchema = z.object({
  primaryObjective: z.enum(['leads', 'meetings', 'sales', 'brand_awareness'], {
    required_error: 'Please select a primary objective',
  }),
  
  timeline: z.string()
    .min(1, 'Please specify a timeline'),
  
  volumeExpectations: z.string()
    .min(1, 'Please specify volume expectations'),
  
  budget: z.string()
    .min(1, 'Please specify a budget range'),
  
  outreachChannels: z.array(z.string())
    .min(1, 'Please select at least one outreach channel'),
  
  additionalNotes: z.string()
    .max(500, 'Additional notes must be less than 500 characters')
    .optional(),
});

export const onboardingFormSchema = z.object({
  companyInformation: companyInformationSchema,
  targetCriteria: targetCriteriaSchema,
  campaignGoals: campaignGoalsSchema,
  isDraft: z.boolean().default(false),
  completedAt: z.date().optional(),
  userId: z.string().optional(),
});

export type CompanyInformationFormData = z.infer<typeof companyInformationSchema>;
export type TargetCriteriaFormData = z.infer<typeof targetCriteriaSchema>;
export type CampaignGoalsFormData = z.infer<typeof campaignGoalsSchema>;
export type OnboardingFormSchema = z.infer<typeof onboardingFormSchema>; 